import { z } from "zod";

export const FX_BLEND_MODES = [
  "normal",
  "multiply",
  "screen",
  "lighten",
  "overlay",
  "soft-light",
  "hard-light",
  "color-dodge",
  "color-burn",
  "add",
  "difference",
  "exclusion",
] as const;

export type FxBlendMode = (typeof FX_BLEND_MODES)[number];

export const FX_CATEGORIES = ["filter", "overlay", "distortion", "body", "audio-reactive"] as const;
export type FxCategory = (typeof FX_CATEGORIES)[number];

export const FX_BODY_EFFECT_TYPES = [
  "motion-trail",
  "outline-glow",
  "shadow-echo",
  "background-blur-only",
  "spotlight",
  "phantom-clone",
  "glow-pulse",
  "energy-aura",
  "edge-neon-pulse",
  "subject-pulse-warp",
  "subject-freeze-frame-highlight",
] as const;

export type FxBodyEffectType = (typeof FX_BODY_EFFECT_TYPES)[number];

export const FX_MASK_TYPES = ["none", "rect", "ellipse", "free-draw", "linear-gradient", "radial-gradient"] as const;
export type FxMaskType = (typeof FX_MASK_TYPES)[number];
export const FX_MASK_PATH_OPERATIONS = ["replace", "add", "subtract", "intersect"] as const;
export type FxMaskPathOperation = (typeof FX_MASK_PATH_OPERATIONS)[number];

export const FX_MODULATION_BANDS = ["low", "mid", "high", "full", "onset", "transient", "beat"] as const;
export type FxModulationBand = (typeof FX_MODULATION_BANDS)[number];

export const FX_MODULATION_MODES = ["beat-only", "continuous"] as const;
export type FxModulationMode = (typeof FX_MODULATION_MODES)[number];

const FxMaskPointHandleSchema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
});

const FxMaskPointSchema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
  cpIn: FxMaskPointHandleSchema.optional(),
  cpOut: FxMaskPointHandleSchema.optional(),
});

const FxMaskPathSchema = z.object({
  id: z.string().min(1),
  points: z.array(FxMaskPointSchema).max(4096),
  closed: z.boolean().default(true),
  operation: z.enum(FX_MASK_PATH_OPERATIONS).default("add"),
});

export const FxMaskSchema = z.object({
  type: z.enum(FX_MASK_TYPES).default("none"),
  featherPx: z.number().finite().min(0).max(300).default(0),
  opacity: z.number().finite().min(0).max(1).default(1),
  invert: z.boolean().default(false),
  expandPx: z.number().finite().min(-500).max(500).default(0),
  paths: z.array(FxMaskPathSchema).max(32).default([]),
});

export type FxMask = z.infer<typeof FxMaskSchema>;
export type FxMaskPath = z.infer<typeof FxMaskPathSchema>;
export type FxMaskPoint = z.infer<typeof FxMaskPointSchema>;

export const FxModulationSchema = z.object({
  enabled: z.boolean().default(false),
  band: z.enum(FX_MODULATION_BANDS).default("full"),
  mode: z.enum(FX_MODULATION_MODES).default("continuous"),
  strength: z.number().finite().min(0).max(1).default(0.5),
  threshold: z.number().finite().min(0).max(1).default(0.1),
  smoothing: z.number().finite().min(0).max(1).default(0.5),
  responseMs: z.number().finite().min(0).max(600).default(90),
  latencyMs: z.number().finite().min(0).max(250).default(0),
  clampMin: z.number().finite().min(0).max(1).default(0),
  clampMax: z.number().finite().min(0).max(1).default(1),
});

export type FxModulation = z.infer<typeof FxModulationSchema>;

export const FX_LAYER_TYPES = ["overlay-video"] as const;
export type FxLayerType = (typeof FX_LAYER_TYPES)[number];
export const FX_OVERLAY_PLACEMENT_MODES = ["follow-target", "screen-space"] as const;
export type FxOverlayPlacementMode = (typeof FX_OVERLAY_PLACEMENT_MODES)[number];

export const FxOverlayVideoLayerSchema = z.object({
  id: z.string().min(1),
  type: z.literal("overlay-video"),
  src: z.string().min(1),
  blendMode: z.enum(FX_BLEND_MODES).default("screen"),
  opacity: z.number().finite().min(0).max(1).default(1),
  startOffset: z.number().finite().min(0).default(0),
  duration: z.number().finite().min(0.001).default(1),
  loop: z.boolean().default(false),
  playbackRate: z.number().finite().min(0.1).max(4).default(1),
  placementMode: z.enum(FX_OVERLAY_PLACEMENT_MODES).default("follow-target"),
  positionX: z.number().finite().min(-4000).max(4000).default(0),
  positionY: z.number().finite().min(-4000).max(4000).default(0),
  positionZ: z.number().finite().min(-400).max(400).default(0),
});

export type FxOverlayVideoLayer = z.infer<typeof FxOverlayVideoLayerSchema>;
export type FxLayer = FxOverlayVideoLayer;

export const FxBlockKeyframeSchema = z.object({
  id: z.string().min(1),
  time: z.number().finite().min(0),
  property: z.string().min(1),
  value: z.number().finite(),
});

export type FxBlockKeyframe = z.infer<typeof FxBlockKeyframeSchema>;

export const FxBlockSchema = z
  .object({
    id: z.string().min(1),
    trackId: z.string().min(1),
    name: z.string().min(1).max(80).default("FX Block"),
    start: z.number().finite().min(0),
    end: z.number().finite().min(0.05),
    enabled: z.boolean().default(true),
    category: z.enum(FX_CATEGORIES).default("overlay"),
    effectType: z.string().min(1).default("light-leak-sweep"),
    opacity: z.number().finite().min(0).max(1).default(1),
    blendMode: z.enum(FX_BLEND_MODES).default("normal"),
    seed: z.number().int().min(0).max(999999999).default(0),
    stackOrder: z.number().int().min(0).max(999).default(0),
    targetClipIds: z.array(z.string().min(1)).optional(),
    targetTrackIds: z.array(z.string().min(1)).optional(),
    preserveDuration: z.boolean().optional(),
    mask: FxMaskSchema.default({ type: "none", featherPx: 0, opacity: 1, invert: false, expandPx: 0, paths: [] }),
    modulation: FxModulationSchema.default({
      enabled: false,
      band: "full",
      mode: "continuous",
      strength: 0.5,
      threshold: 0.1,
      smoothing: 0.5,
      responseMs: 90,
      latencyMs: 0,
      clampMin: 0,
      clampMax: 1,
    }),
    params: z.record(z.string(), z.number().finite()).default({}),
    layers: z.array(FxOverlayVideoLayerSchema).default([]),
    keyframes: z.array(FxBlockKeyframeSchema).default([]),
  })
  .superRefine((value, ctx) => {
    if (value.end <= value.start) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "FxBlock end must be greater than start.",
      });
    }
  });

export type FxBlock = z.infer<typeof FxBlockSchema>;

export const DEFAULT_FX_BLOCK: Omit<FxBlock, "id" | "trackId" | "start" | "end"> = {
  name: "FX Block",
  enabled: true,
  category: "overlay",
  effectType: "light-leak-sweep",
  opacity: 1,
  blendMode: "normal",
  seed: 0,
  stackOrder: 0,
  mask: { type: "none", featherPx: 0, opacity: 1, invert: false, expandPx: 0, paths: [] },
  modulation: {
    enabled: false,
    band: "full",
    mode: "continuous",
    strength: 0.5,
    threshold: 0.1,
    smoothing: 0.5,
    responseMs: 90,
    latencyMs: 0,
    clampMin: 0,
    clampMax: 1,
  },
  params: {},
  layers: [],
  keyframes: [],
};

export function normalizeFxBlocks(input: unknown): FxBlock[] {
  if (!Array.isArray(input)) return [];
  const out: FxBlock[] = [];
  for (const item of input) {
    const parsed = FxBlockSchema.safeParse(item);
    if (parsed.success) out.push(parsed.data);
  }
  return out;
}

export function sortFxBlocksByTrackOrder(fxBlocks: FxBlock[], trackOrderById: Map<string, number>): FxBlock[] {
  return [...fxBlocks].sort((a, b) => {
    const ao = trackOrderById.get(a.trackId) ?? 0;
    const bo = trackOrderById.get(b.trackId) ?? 0;
    if (ao !== bo) return ao - bo;
    if (a.stackOrder !== b.stackOrder) return a.stackOrder - b.stackOrder;
    if (a.start !== b.start) return a.start - b.start;
    return a.id.localeCompare(b.id);
  });
}
